import React from 'react';
import type {CSSProperties, ReactNode} from 'react';
import {AppLink as Link} from '../app/navigation/index.js';
import {postComment, editComment, deleteComment, listCommentEdits, COMMENT_CHANGE_EVENT} from '../lib/commentsApi.js';
import {renderMentionSegments} from '../lib/activityApi.js';
import {fmtCentralDateTime} from '../lib/dateUtils.js';
import {uploadCommentAttachment, MAX_COMMENT_ATTACHMENTS} from '../lib/commentAttachments.js';
import {imageAltText} from '../lib/imageAlt.js';
import MentionTextarea from './MentionTextarea.tsx';
import type {MentionableProfile} from './MentionTextarea.tsx';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {AuthState} from '../contexts/AuthContext.jsx';
import type {CommentAttachmentMetadata} from '../lib/commentAttachments.js';
import {
  commentAttachmentUrlFor,
  useRecordCollaborationCommentAttachmentUrls,
} from './recordCollaborationAttachmentData.js';
import type {CommentAttachmentUrlProjection} from './recordCollaborationAttachmentData.js';

// ----------------------------------------------------------------------------
// The rows this section renders.
//
// They arrive through RecordCollaborationSection's query owner from SECURITY
// DEFINER RPCs declared as `Promise<unknown[]>`: the client has no generated
// database types and nothing validates the payload. These types are therefore
// DOCUMENTED CONTRACTS, not verified ones; they pin only the members this UI
// consults and the action each one must support.
// ----------------------------------------------------------------------------

// A comment's jsonb attachments entry. Written by uploadCommentAttachment as a
// full CommentAttachmentMetadata, but rows imported from Asana carry a `bucket`
// that the upload path never writes -- so every member is optional, and
// `bucket` exists here while being absent from CommentAttachmentMetadata.
export type CommentAttachmentRef = {
  path?: string;
  name?: string;
  bucket?: string;
  is_image?: boolean;
};

export type CommentRow = {
  id: string;
  body?: string | null;
  created_at?: string | null;
  edited_at?: string | null;
  deleted_at?: string | null;
  author_profile_id?: string | null;
  author_display_name?: string | null;
  source?: string | null;
  is_imported?: boolean | null;
  // Set by the server when the caller may not see the author's name.
  redacted?: boolean | null;
  // The two parallel arrays mig 060 returns for chip rendering: same length,
  // same order.
  mentions?: string[] | null;
  mentioned_profile_names?: string[] | null;
  attachments?: CommentAttachmentRef[] | null;
};

export type CommentEditRow = {
  id: string;
  editor_display_name?: string | null;
  edited_at?: string | null;
  previous_body?: string | null;
  previous_attachments?: CommentAttachmentRef[] | null;
};

// The composer's state, and the same object MentionTextarea emits from
// onChange -- which is why `setDraft(next)` can take its argument whole.
type CommentDraft = {body: string; mentions: string[]};
// The editor adds the comment's existing attachments so they can be dropped
// before saving. Optional because the initial state has none, which is also
// why the removal handler reads it as `prev.attachments || []`.
type CommentEditDraft = CommentDraft & {attachments?: CommentAttachmentRef[]};

// ONE JSX call site, RecordCollaborationSection, owns the three mount reads
// and passes their projections here. This component keeps the action-time
// comment mutations, attachment signing, and edit-history read because each is
// initiated by a control in this UI rather than by the record mount. Automatic
// attachment URL reads are the exception: their component-scoped Query owner
// is recordCollaborationAttachmentData, not this presenter.
type CommentsSectionProps = {
  sb: SupabaseClient;
  authState: AuthState;
  entityType: string;
  entityId: string;
  // Matches PostCommentInput.entityLabel in src/lib/commentsApi.ts, which is
  // where this value ends up -- via `entityLabel || entityId`, the fallback
  // that makes it nullable in the first place.
  entityLabel?: string | null;
  comments: CommentRow[];
  commentsLoading: boolean;
  commentsError: unknown;
  mentionableProfiles: unknown[];
  mentionableProfilesError: unknown;
  refreshComments: () => Promise<unknown>;
  legacyNote?: string;
  fullYearDates?: boolean;
};

// `e?.message || <fallback>`, reproduced exactly for a value that is `unknown`
// in a strict catch: a truthy `.message` wins and anything else falls back,
// with no shape asserted onto something that genuinely could be anything
// thrown. The same helper src/lib/entityMutations.ts and
// src/shared/PrivateStorageObject.tsx each needed.
function errorMessage(e: unknown): string {
  const message = e && typeof e === 'object' && 'message' in e ? (e as {message?: unknown}).message : undefined;
  return message ? String(message) : '';
}

const SECTION: CSSProperties = {
  background: 'white',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 14,
  fontFamily: 'inherit',
};
const HEADER: CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: 'var(--ink)',
  marginBottom: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};
const COUNT_BADGE: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ink-muted)',
  background: 'var(--divider)',
  borderRadius: 999,
  padding: '1px 7px',
};
const POST_BTN: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 10,
  border: '1px solid #187A54',
  background: '#187A54',
  color: 'white',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
};
const POST_BTN_DISABLED: CSSProperties = {
  ...POST_BTN,
  background: '#9ca3af',
  borderColor: '#9ca3af',
  cursor: 'not-allowed',
};
const COMMENT_ROW: CSSProperties = {
  padding: '10px 0',
  borderBottom: '1px solid var(--divider)',
};
const COMMENT_HEAD: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
  fontSize: 12,
  color: 'var(--ink-muted)',
  flexWrap: 'wrap',
};
const AUTHOR: CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--ink)',
};
const BODY: CSSProperties = {
  fontSize: 13,
  color: 'var(--ink)',
  marginTop: 4,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  lineHeight: 1.45,
};
const MENTION_CHIP: CSSProperties = {
  display: 'inline-block',
  padding: '0 4px',
  borderRadius: 999,
  background: '#fef3c7',
  color: '#92400e',
  fontWeight: 600,
};
const DELETED_PLACEHOLDER: CSSProperties = {
  padding: '8px 0',
  borderBottom: '1px solid var(--divider)',
  fontSize: 13,
  color: 'var(--ink-faint)',
  fontStyle: 'italic',
};
const LINK_BTN: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--ink-muted)',
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'inherit',
  padding: 0,
  textDecoration: 'underline',
};
const ACTION_BTN: CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'inherit',
  padding: 0,
};
const EDIT_HISTORY: CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 10,
  marginTop: 6,
  fontSize: 12,
  maxHeight: 200,
  overflowY: 'auto',
};

// Posted/edited/deleted stamps render the absolute farm time
// (America/Chicago via fmtCentralDateTime — Comments follow the same
// Central-time lock as Tasks) so every comment shows a readable timestamp
// without hover. Fresh comments (<24h) append a short relative age so a
// live discussion still reads at a glance.
function fmtPostedAt(iso: string | null | undefined, fullYear = false): string {
  const absolute = fmtCentralDateTime(iso, {fullYear});
  // Bail before the relative-age math when there is no timestamp. Every absent
  // input already reached `return absolute` at the bottom: `new Date(undefined)`
  // and `new Date('')` are Invalid Dates, so all three comparisons below are
  // false, and `new Date(null)` is the epoch, which is never under 24h old.
  // Same string out for every input, without handing a Date constructor a value
  // it does not accept.
  if (!iso) return absolute;
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    // diff < 60 (not 0..60) so server-stamped times slightly ahead of the
    // client clock still read 'just now' instead of dropping the suffix.
    if (diff < 60) return absolute + ' · just now';
    if (diff < 3600) return absolute + ' · ' + Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return absolute + ' · ' + Math.floor(diff / 3600) + 'h ago';
  } catch (_e) {
    /* absolute stamp alone */
  }
  return absolute;
}

const URL_RE = /(https?:\/\/[^\s<>"')\]]+)/g;

function renderCommentBody(
  body: string | null | undefined,
  mentions: string[] | null | undefined,
  mentionedProfileNames: string[] | null | undefined,
): ReactNode[] | null {
  if (!body) return null;
  const mentionNames = mentionedProfileNames || (mentions || []).map(() => '');
  const mentionIds = mentions || [];
  const segs = renderMentionSegments(body, mentionNames, mentionIds);
  const result: ReactNode[] = [];
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    if (s.type === 'mention') {
      result.push(
        <span key={i} style={MENTION_CHIP}>
          @{s.display}
        </span>,
      );
    } else {
      const parts = (s.text || '').split(URL_RE);
      for (let j = 0; j < parts.length; j++) {
        if (URL_RE.test(parts[j])) {
          URL_RE.lastIndex = 0;
          result.push(
            <a
              key={`${i}-${j}`}
              href={parts[j]}
              target="_blank"
              rel="noopener noreferrer"
              style={{color: 'var(--brand)'}}
            >
              {parts[j]}
            </a>,
          );
        } else {
          result.push(<span key={`${i}-${j}`}>{parts[j]}</span>);
        }
      }
    }
  }
  return result;
}

export default function CommentsSection({
  sb,
  authState,
  entityType,
  entityId,
  entityLabel,
  comments,
  commentsLoading,
  commentsError,
  mentionableProfiles,
  mentionableProfilesError,
  refreshComments,
  legacyNote,
  fullYearDates = false,
}: CommentsSectionProps) {
  const callerProfileId = authState && authState.user ? authState.user.id : null;
  // Was `authState?.role`. `?.` only short-circuits null and undefined, so on
  // the signed-out `false` it evaluated `false.role` and produced `undefined`;
  // this ternary produces `undefined` for exactly the same inputs and an
  // identical `role` for the identity. Same value for every input, and it does
  // not require pretending `false` has a `role`.
  const callerRole = authState ? authState.role : undefined;
  const isAdmin = callerRole === 'admin';

  const [posting, setPosting] = React.useState(false);
  const [draft, setDraft] = React.useState<CommentDraft>({body: '', mentions: []});
  const [notice, setNotice] = React.useState<string | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState<CommentEditDraft>({body: '', mentions: []});
  const [editHistoryId, setEditHistoryId] = React.useState<string | null>(null);
  const [editHistory, setEditHistory] = React.useState<CommentEditRow[]>([]);
  const [expandDeletedId, setExpandDeletedId] = React.useState<string | null>(null);
  const [draftFiles, setDraftFiles] = React.useState<File[]>([]);
  const [editFiles, setEditFiles] = React.useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // The RPC payload remains deliberately narrow at the presentation boundary:
  // these are only the optional members MentionTextarea consults, and no row is
  // added, dropped, or rewritten except the existing self-filter.
  const mentionProfiles = React.useMemo(
    () => (mentionableProfiles as MentionableProfile[]).filter((p) => p.id !== callerProfileId),
    [callerProfileId, mentionableProfiles],
  );
  // The owner sees the rendered comment rows, filters deleted attachments, and
  // gives every thumbnail a scoped Query projection. It is deliberately called
  // once per CommentsSection rather than once per row child.
  const attachmentUrls = useRecordCollaborationCommentAttachmentUrls({sb, authState, comments});

  const showLegacyNote =
    entityType === 'pig.breeder' &&
    typeof legacyNote === 'string' &&
    legacyNote.length > 0 &&
    !comments.some((c) => c.source === 'breeder_note' && c.is_imported && c.body === legacyNote);
  const count = comments.filter((c) => !c.deleted_at).length + (showLegacyNote ? 1 : 0);

  React.useEffect(() => {
    if (!commentsError) return;
    if (typeof console !== 'undefined')
      console.warn('CommentsSection: load failed', errorMessage(commentsError) || commentsError);
  }, [commentsError]);

  React.useEffect(() => {
    if (!mentionableProfilesError || typeof console === 'undefined' || !console.warn) return;
    const message = errorMessage(mentionableProfilesError);
    console.warn('MentionTextarea: profile load failed', message || mentionableProfilesError);
  }, [mentionableProfilesError]);

  React.useEffect(() => {
    function onCommentChange() {
      void refreshComments();
    }
    window.addEventListener(COMMENT_CHANGE_EVENT, onCommentChange);
    return () => window.removeEventListener(COMMENT_CHANGE_EVENT, onCommentChange);
  }, [refreshComments]);

  async function onPost() {
    if (!draft.body.trim() || posting) return;
    setPosting(true);
    setNotice(null);
    try {
      const attachments: CommentAttachmentMetadata[] = [];
      for (let i = 0; i < draftFiles.length; i++) {
        const meta = await uploadCommentAttachment(
          sb,
          entityType,
          entityId,
          'cmt-' + Date.now() + '-' + (i + 1),
          draftFiles[i],
        );
        attachments.push(meta);
      }
      await postComment(sb, {
        entityType,
        entityId,
        body: draft.body,
        entityLabel: entityLabel || entityId,
        mentions: draft.mentions,
        attachments,
      });
      setDraft({body: '', mentions: []});
      setDraftFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refreshComments();
    } catch (e) {
      setNotice('Could not post. ' + errorMessage(e));
    } finally {
      setPosting(false);
    }
  }

  function startEdit(c: CommentRow) {
    setEditingId(c.id);
    setEditDraft({body: c.body || '', mentions: c.mentions || [], attachments: c.attachments || []});
    setEditFiles([]);
  }

  async function saveEdit() {
    if (!editDraft.body.trim()) return;
    try {
      const existingAtts = editDraft.attachments || [];
      const newAtts: CommentAttachmentMetadata[] = [];
      for (let i = 0; i < editFiles.length; i++) {
        if (existingAtts.length + newAtts.length >= MAX_COMMENT_ATTACHMENTS) break;
        const meta = await uploadCommentAttachment(
          sb,
          entityType,
          entityId,
          'cmt-edit-' + Date.now() + '-' + (i + 1),
          editFiles[i],
        );
        newAtts.push(meta);
      }
      await editComment(sb, {
        // Non-null on this path by construction: saveEdit is reachable only
        // from the editor, which startEdit opened by setting editingId to a
        // comment id. `?? ''` records that without an assertion -- an empty id
        // would reach the same server-side rejection any bad id does.
        commentId: editingId ?? '',
        body: editDraft.body,
        mentions: editDraft.mentions,
        attachments: [...existingAtts, ...newAtts],
      });
      setEditingId(null);
      setEditFiles([]);
      await refreshComments();
    } catch (e) {
      setNotice('Could not save edit. ' + errorMessage(e));
    }
  }

  async function onDelete(commentId: string) {
    try {
      await deleteComment(sb, commentId);
      await refreshComments();
    } catch (e) {
      setNotice('Could not delete. ' + errorMessage(e));
    }
  }

  async function toggleEditHistory(commentId: string) {
    if (editHistoryId === commentId) {
      setEditHistoryId(null);
      return;
    }
    try {
      const edits = (await listCommentEdits(sb, commentId)) as CommentEditRow[];
      setEditHistory(edits);
      setEditHistoryId(commentId);
    } catch (_e) {
      setEditHistoryId(null);
    }
  }

  if (!entityType || !entityId) return null;

  return (
    <div
      data-comments-section="1"
      data-comments-entity-type={entityType}
      data-comments-entity-id={entityId}
      style={SECTION}
    >
      <div style={HEADER}>
        <span>Comments</span>
        <span style={COUNT_BADGE}>{count}</span>
      </div>

      {notice && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 10,
            padding: '6px 10px',
            fontSize: 12,
            color: '#b91c1c',
            marginBottom: 8,
          }}
        >
          {notice}
          <button
            type="button"
            onClick={() => setNotice(null)}
            style={{...ACTION_BTN, color: '#b91c1c', marginLeft: 8}}
          >
            ×
          </button>
        </div>
      )}

      <div data-comments-compose="1" style={{marginBottom: 12}}>
        <MentionTextarea
          sb={sb}
          value={draft.body}
          mentions={draft.mentions}
          onChange={(next) => setDraft(next)}
          placeholder="Add a comment, or @ to mention…"
          disabled={posting}
          profiles={mentionProfiles}
        />
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, gap: 8}}>
          <div style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-muted)'}}>
            <label style={{cursor: 'pointer', color: 'var(--brand)', fontSize: 12}}>
              📎 Attach
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
                multiple
                style={{display: 'none'}}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []).slice(0, MAX_COMMENT_ATTACHMENTS);
                  setDraftFiles(files);
                }}
              />
            </label>
            {draftFiles.length > 0 && (
              <span>
                {draftFiles.length} photo{draftFiles.length > 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          <button
            type="button"
            data-comments-post-button="1"
            onClick={onPost}
            disabled={posting || !draft.body.trim()}
            style={posting || !draft.body.trim() ? POST_BTN_DISABLED : POST_BTN}
          >
            {posting ? 'Posting…' : 'Post'}
          </button>
        </div>
      </div>

      <div data-comments-list="1">
        {commentsLoading && comments.length === 0 && (
          <div style={{fontSize: 12, color: 'var(--ink-muted)', padding: '8px 0'}}>Loading…</div>
        )}
        {!commentsLoading && comments.length === 0 && !showLegacyNote && (
          <div data-comments-empty="1" style={{fontSize: 13, color: 'var(--ink-muted)', padding: '8px 0'}}>
            No comments yet.
          </div>
        )}
        {showLegacyNote && (
          <div data-comment-legacy-note="1" style={COMMENT_ROW}>
            <div style={COMMENT_HEAD}>Imported note · Original author and date unknown</div>
            <div style={BODY}>{renderCommentBody(legacyNote, [], [])}</div>
          </div>
        )}
        {comments.map((c) => {
          const isDeleted = !!c.deleted_at;
          const isAuthor = c.author_profile_id === callerProfileId;
          const isEditing = editingId === c.id;
          // Cattle Log mirrors ('clog-' ids, migration 112) are system-managed
          // copies of /cattle/log entries: read-only here (the RPCs also
          // reject edit/delete server-side) with visible provenance.
          const isCattleLogMirror = typeof c.id === 'string' && c.id.startsWith('clog-');
          const isImportedBreederNote = c.source === 'breeder_note' && c.is_imported === true;

          if (isDeleted && !isAdmin) {
            return (
              <div
                key={c.id}
                id={'comment-' + c.id}
                data-comment-id={c.id}
                data-comment-deleted="1"
                style={DELETED_PLACEHOLDER}
              >
                (Comment deleted)
              </div>
            );
          }

          if (isDeleted && isAdmin) {
            const expanded = expandDeletedId === c.id;
            return (
              <div
                key={c.id}
                id={'comment-' + c.id}
                data-comment-id={c.id}
                data-comment-deleted="1"
                style={DELETED_PLACEHOLDER}
              >
                (Comment deleted)
                <button
                  type="button"
                  onClick={() => setExpandDeletedId(expanded ? null : c.id)}
                  style={{...LINK_BTN, marginLeft: 8}}
                >
                  {expanded ? 'hide' : 'view'}
                </button>
                {expanded && (
                  <div style={{marginTop: 6, color: 'var(--ink-muted)', fontStyle: 'normal'}}>
                    <div style={{fontSize: 11, marginBottom: 2}}>
                      by {c.author_display_name || 'Unknown'} · deleted{' '}
                      {fmtCentralDateTime(c.deleted_at, {fullYear: fullYearDates})}
                    </div>
                    <div style={{...BODY, color: 'var(--ink-muted)'}}>{c.body}</div>
                  </div>
                )}
              </div>
            );
          }

          return (
            <div key={c.id} id={'comment-' + c.id} data-comment-id={c.id} data-comment-deleted="0" style={COMMENT_ROW}>
              <div style={COMMENT_HEAD}>
                {isImportedBreederNote ? (
                  <span data-comment-imported-note="1">Imported note · Original author and date unknown</span>
                ) : (
                  <>
                    <span style={AUTHOR}>{c.redacted ? '(redacted)' : c.author_display_name || 'Unknown user'}</span>
                    <span data-comment-posted-at="1">· {fmtPostedAt(c.created_at, fullYearDates)}</span>
                  </>
                )}
                {isCattleLogMirror && (
                  // Client-side navigation (CommentsSection only mounts
                  // inside the router) — a raw <a href> would full-reload.
                  <Link
                    data-comment-cattle-log-origin="1"
                    to="/cattle/log"
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#065f46',
                      background: '#ecfdf5',
                      border: '1px solid #a7f3d0',
                      borderRadius: 999,
                      padding: '1px 8px',
                      textDecoration: 'none',
                    }}
                  >
                    From Cattle Log
                  </Link>
                )}
                {c.edited_at &&
                  (isCattleLogMirror ? (
                    // Mirrors carry edited_at from resync, but their edit-
                    // history rows live on the ORIGINAL entry id — a toggle
                    // here would open nothing, so render plain text.
                    <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>edited</span>
                  ) : (
                    <button type="button" onClick={() => toggleEditHistory(c.id)} style={LINK_BTN}>
                      edited
                    </button>
                  ))}
                {!isDeleted && isAuthor && !isEditing && !isCattleLogMirror && !isImportedBreederNote && (
                  <>
                    <button type="button" onClick={() => startEdit(c)} style={{...ACTION_BTN, color: 'var(--brand)'}}>
                      Edit
                    </button>
                    <button type="button" onClick={() => onDelete(c.id)} style={{...ACTION_BTN, color: '#b91c1c'}}>
                      Delete
                    </button>
                  </>
                )}
                {!isDeleted && isAdmin && !isAuthor && !isCattleLogMirror && !isImportedBreederNote && (
                  <button
                    type="button"
                    onClick={() => onDelete(c.id)}
                    style={{...ACTION_BTN, color: '#b91c1c', marginLeft: 'auto'}}
                  >
                    Delete
                  </button>
                )}
              </div>

              {isEditing ? (
                <div style={{marginTop: 6}}>
                  <MentionTextarea
                    sb={sb}
                    value={editDraft.body}
                    mentions={editDraft.mentions}
                    onChange={(next) => setEditDraft((prev) => ({...prev, body: next.body, mentions: next.mentions}))}
                    placeholder="Edit comment…"
                    profiles={mentionProfiles}
                  />
                  {Array.isArray(editDraft.attachments) && editDraft.attachments.length > 0 && (
                    <div style={{display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6}}>
                      {editDraft.attachments.map((att, ai) => (
                        <span
                          key={ai}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '2px 8px',
                            borderRadius: 10,
                            border: '1px solid var(--border)',
                            background: 'var(--divider)',
                            fontSize: 11,
                          }}
                        >
                          {att.is_image ? '📷' : '📄'} {att.name || 'file'}
                          <button
                            type="button"
                            onClick={() =>
                              setEditDraft((prev) => ({
                                ...prev,
                                attachments: (prev.attachments || []).filter((_, i) => i !== ai),
                              }))
                            }
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#b91c1c',
                              cursor: 'pointer',
                              fontSize: 11,
                              padding: 0,
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{display: 'flex', gap: 6, marginTop: 6, alignItems: 'center'}}>
                    <button type="button" onClick={saveEdit} style={POST_BTN}>
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      style={{
                        ...POST_BTN,
                        background: 'white',
                        color: 'var(--ink)',
                        borderColor: 'var(--border-strong)',
                      }}
                    >
                      Cancel
                    </button>
                    <label style={{cursor: 'pointer', color: 'var(--brand)', fontSize: 12, marginLeft: 'auto'}}>
                      📎 Add
                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
                        multiple
                        style={{display: 'none'}}
                        onChange={(e) =>
                          setEditFiles(Array.from(e.target.files || []).slice(0, MAX_COMMENT_ATTACHMENTS))
                        }
                      />
                    </label>
                    {editFiles.length > 0 && (
                      <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>
                        {editFiles.length} new file{editFiles.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div style={BODY}>{renderCommentBody(c.body, c.mentions, c.mentioned_profile_names)}</div>
                  {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                    <div style={{display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6}}>
                      {c.attachments.map((att, ai) => (
                        <CommentAttachmentThumb
                          key={ai}
                          att={att}
                          attachment={commentAttachmentUrlFor(attachmentUrls, att)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {editHistoryId === c.id && editHistory.length > 0 && (
                <div style={EDIT_HISTORY}>
                  <div style={{fontWeight: 600, marginBottom: 4, fontSize: 11, color: '#1A1F24'}}>Edit history</div>
                  {editHistory.map((e) => (
                    <div key={e.id} style={{padding: '4px 0', borderBottom: '1px solid #E6E8EB'}}>
                      <div style={{fontSize: 11, color: '#68707A'}}>
                        {e.editor_display_name || 'Unknown'} ·{' '}
                        {fmtCentralDateTime(e.edited_at, {fullYear: fullYearDates})}
                      </div>
                      <div style={{fontSize: 12, color: '#1A1F24', whiteSpace: 'pre-wrap', marginTop: 2}}>
                        {e.previous_body}
                      </div>
                      {Array.isArray(e.previous_attachments) && e.previous_attachments.length > 0 && (
                        <div style={{fontSize: 11, color: '#68707A', marginTop: 2}}>
                          Attachments: {e.previous_attachments.map((a) => a.name || 'file').join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A private child of this section -- never exported, never rendered anywhere
// else. It receives a settled Query projection only; the component-scoped
// attachment owner performs every automatic storage read and refresh.
type CommentAttachmentThumbProps = {
  att: CommentAttachmentRef;
  attachment: CommentAttachmentUrlProjection;
};

function CommentAttachmentThumb({att, attachment}: CommentAttachmentThumbProps) {
  const url = attachment.url;
  const isImage = att.is_image || /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(att.path || '');

  if (!isImage) {
    return (
      <a
        href={url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        data-comment-attachment={att.path}
        data-comment-attachment-state={url ? 'ready' : attachment.loading ? 'loading' : 'unavailable'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 8px',
          borderRadius: 10,
          border: '1px solid var(--border)',
          background: 'var(--divider)',
          fontSize: 11,
          color: 'var(--brand)',
          textDecoration: 'none',
        }}
      >
        📄 {att.name || 'document'}
      </a>
    );
  }

  return (
    <a
      href={url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      data-comment-attachment={att.path}
      data-comment-attachment-state={url ? 'ready' : attachment.loading ? 'loading' : 'unavailable'}
      style={{
        display: 'block',
        width: 60,
        height: 60,
        borderRadius: 10,
        overflow: 'hidden',
        border: '1px solid var(--border)',
        background: 'var(--divider)',
        flexShrink: 0,
      }}
    >
      {url ? (
        <img
          src={url}
          alt={imageAltText(att.name, {fallback: 'Comment attachment image'})}
          style={{width: '100%', height: '100%', objectFit: 'cover'}}
        />
      ) : (
        <span
          style={{
            fontSize: 10,
            color: 'var(--ink-faint)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          📷
        </span>
      )}
    </a>
  );
}
